import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { LoadingData } from 'services/types';
import { AccountHolderApi } from 'common/types/adyen';
import { getAccountHolder } from 'common/api/frontend';
import errorHandler from 'common/services/errorHandler';
import { t, useTranslation } from 'services/localization';
import * as ShopSelectors from 'selectors/ShopSelectors';
import ContainerBox from 'components/ContainerBox';
import { Box, Button, Grid, Typography } from '@mui/material';
import { RentlePaymentsStatus } from './components/RentlePaymentsStatus';
import { PaymentSetupDialog } from './PaymentSetupDialog/index';
import RentlePaymentsLogo from './components/RentlePaymentsLogo';
import { PaymentBlockSkeleton } from '../components/PaymentBlockSkeleton';
import { useVisibilityChange } from 'common/hooks/useVisibilityChange';
import { ActionRequiredBanner } from './components/ActionRequiredBanner';
import PaymentMethods from './components/RentlePaymentMethods';
import { PaymentMethodIntro } from '../components/PaymentMethodIntro';
import { PaymentProviders } from 'common/modules/payments/constants';

type RentlePaymentsProps = {
	isSupported: boolean;
};

const RentlePayments = (props: RentlePaymentsProps) => {
	const { isSupported } = props;
	const shopReadOnly = useSelector(ShopSelectors.activeShopReadOnly);
	const [accountHolder, setAccountHolder] = useState<LoadingData<AccountHolderApi | null>>({
		loading: true,
		error: null,
		data: null,
	});
	const [paymentSetupOpen, setPaymentSetupOpen] = useState<boolean>(false);
	const accountHolderCode = shopReadOnly?.integrations?.ADYEN?.accountHolderCode;

	useEffect(() => {
		fetchShopPaymentData(accountHolderCode);
	}, [accountHolderCode]);

	useVisibilityChange({
		hidden: useCallback(() => fetchShopPaymentData(accountHolderCode), [accountHolderCode]),
	});

	const fetchShopPaymentData = async (accountHolderCode: string | undefined) => {
		try {
			if (!accountHolderCode) {
				setAccountHolder({
					loading: false,
					error: null,
					data: null,
				});
				return;
			}
			const accountHolder = await getAccountHolder({ accountHolderCode });
			setAccountHolder({
				loading: false,
				error: null,
				data: accountHolder,
			});
		} catch (e) {
			errorHandler.report(e);
			setAccountHolder({
				loading: false,
				error: e.message,
				data: null,
			});
		}
	};

	const noAccountHolder = !accountHolderCode;

	return (
		<ContainerBox relative>
			<Grid
				container
				justifyContent="space-between"
				alignItems={!isSupported ? 'flex-start' : noAccountHolder ? 'center' : 'flex-end'}
				spacing={1}
			>
				<Grid
					item
					xs={6}
					sx={{
						opacity: !isSupported ? 0.5 : 1,
					}}
				>
					<RentlePaymentsLogo />
				</Grid>
				<Grid item xs={6} textAlign="right">
					{!isSupported ? (
						<Typography>
							{t(
								'account.payments.notSupportedYetInYourRegion',
								'Not supported yet in your region',
							)}
						</Typography>
					) : noAccountHolder ? (
						<CompleteSetupBtn
							onClick={() => {
								setPaymentSetupOpen(true);
							}}
						/>
					) : !!accountHolder.data ? (
						<RentlePaymentsStatus account={accountHolder.data} />
					) : null}
				</Grid>
			</Grid>
			<Content>
				{isSupported ? (
					<>
						{!!accountHolder.data && <ActionRequiredBanner account={accountHolder.data} />}
						<Box mt={3} />
						{noAccountHolder ? (
							<PaymentMethodIntro
								paymentProvider={PaymentProviders.adyen}
								paymentMethods={`Visa, MasterCard, ApplePay, GooglePay, ${t(
									'account.payments.localPaymentMethods',
									'Local payment methods',
								)}`}
								description={t(
									'account.payments.rentlePaymentsIntro',
									'Effortless payments for your online store with deposits',
								)}
								deposits={true}
							/>
						) : accountHolder.data ? (
							<PaymentMethods />
						) : accountHolder.loading ? (
							<PaymentBlockSkeleton rows={2} />
						) : (
							<PaymentError />
						)}
					</>
				) : (
					<Typography variant="body2" color="textSecondary">
						{t(
							'account.payments.rentlePaymentsIntro',
							'Effortless payments for your online store with deposits',
						)}
					</Typography>
				)}
			</Content>
			<PaymentSetupDialog open={paymentSetupOpen} onClose={() => setPaymentSetupOpen(false)} />
		</ContainerBox>
	);
};

const Content: React.FC<{}> = ({ children }) => <Box mt={3}>{children}</Box>;

const PaymentError = () => {
	const { t } = useTranslation();
	return (
		<Typography variant="h6" color="secondary">
			{t('common:errors.errorFetchingPaymentInfo', 'Error fetching payment information')}
		</Typography>
	);
};

const CompleteSetupBtn = ({ onClick }: { onClick: () => void }) => {
	const { t } = useTranslation();
	return (
		<Button color="primary" variant="contained" onClick={onClick}>
			{t('settings.completeSetup', 'Complete setup')}
		</Button>
	);
};

export default RentlePayments;
